import { Abi, ContractFunctionName, TransactionReceipt } from 'viem'
import { UseSimulateContractParameters } from 'wagmi'

import type { UseWriteOptions, UseWriteResult, WriteStatus } from './useWrite'
import { useWrite } from './useWrite'
import { useWalletType } from '@/domain/hooks/useWalletType'
import { useEip5792Write } from '@/features/actions/logic/transactions/useEip5792Write'
import type { GetWriteConfigResult } from '@/features/actions/logic/types'

export interface UseRegularOrEip5792WriteResult {
  reset: () => void
  write: () => void
  status: WriteStatus | { kind: 'wallet-type-pending' }
}

/**
 * Routes single writes between regular write flow and EIP-5792 flow.
 * EIP-5792 single-write mode is used only for Gnosis Safe wallets.
 */
export function useRegularOrEip5792Write<
  TAbi extends Abi,
  TFunctionName extends ContractFunctionName<TAbi, 'nonpayable' | 'payable'>,
>(
  args: UseSimulateContractParameters<TAbi, TFunctionName> & { enabled?: boolean },
  options: UseWriteOptions = {},
): UseRegularOrEip5792WriteResult {
  const { walletType, isPending: isWalletTypePending } = useWalletType()
  const isGnosisSafe = walletType === 'gnosis-safe'
  const enabled = args.enabled ?? true
  const canUseEip5792 = !!args.address && !!args.abi && !!args.functionName

  const eip5792Contracts: GetWriteConfigResult[] = canUseEip5792
    ? [
        {
          address: args.address!,
          abi: args.abi!,
          functionName: args.functionName!,
          args: args.args as readonly unknown[] | undefined,
          value: args.value,
        },
      ]
    : []

  const regularWrite = useWrite(
    {
      ...args,
      enabled: enabled && !isGnosisSafe,
    },
    options,
  )

  const eip5792SafeWriteRaw = useEip5792Write({
    contracts: eip5792Contracts,
    enabled: enabled && isGnosisSafe && canUseEip5792,
    callbacks: {
      onTransactionSettled: {
        mounted: (txReceipts) => {
          const txReceipt = txReceipts[0]
          if (txReceipt) {
            options.onTransactionSettled?.mounted?.(txReceipt as unknown as TransactionReceipt)
          }
        },
        unmounted: options.onTransactionSettled?.unmounted,
      },
    },
    onBeforeWrite: options.onBeforeWrite,
    mutationKeyPrefix: options.mutationKeyPrefix,
  })

  const eip5792SafeStatus = ((): WriteStatus => {
    if (eip5792SafeWriteRaw.status.kind === 'success') {
      return { kind: 'success' }
    }
    if (eip5792SafeWriteRaw.status.kind === 'tx-sending') {
      return { kind: 'tx-sending' }
    }
    if (eip5792SafeWriteRaw.status.kind === 'tx-confirming') {
      return { kind: 'tx-mining' }
    }
    if (!enabled) {
      return { kind: 'disabled' }
    }
    if (eip5792SafeWriteRaw.status.kind === 'error') {
      if (eip5792SafeWriteRaw.status.errorKind === 'submission') {
        return { kind: 'error', errorKind: 'tx-submission', error: eip5792SafeWriteRaw.status.error }
      }
      if (eip5792SafeWriteRaw.status.errorKind === 'wallet-upgrade-rejection') {
        return { kind: 'error', errorKind: 'tx-submission', error: eip5792SafeWriteRaw.status.error }
      }
      if (eip5792SafeWriteRaw.status.errorKind === 'before-write') {
        return { kind: 'error', errorKind: 'before-write', error: eip5792SafeWriteRaw.status.error }
      }
      return { kind: 'error', errorKind: 'tx-reverted', error: eip5792SafeWriteRaw.status.error }
    }

    return { kind: 'ready' }
  })()

  const eip5792SafeWrite: UseWriteResult = {
    reset: eip5792SafeWriteRaw.reset,
    write: eip5792SafeWriteRaw.write,
    resimulate: () => {},
    status: eip5792SafeStatus,
  }

  const writeResult = isGnosisSafe ? eip5792SafeWrite : regularWrite
  const statusOverride = isWalletTypePending ? { kind: 'wallet-type-pending' as const } : undefined
  return {
    ...writeResult,
    status: statusOverride ?? writeResult.status,
  }
}
